import {
  Breadcrumbs,
  type BreadcrumbItem,
} from "@repo/ui/Navigation/breadcrumbs";
import { useRef, type FC } from "react";
import { useMatch, useMatches, type UIMatch } from "react-router";
import z from "zod";
import { useMainNavigationFeatureFlags } from "../../helpers/mainNavigation.js";
import { checkIsMatchWithNavigation } from "../appNavigationContainer.js";
import { appPaths } from "../appPaths.js";
import { useBreadcrumbDocumentTitle } from "./documentTitle.js";
import { useProjectWorkspaceCrumbItem } from "./projectWorkspaceCrumb.js";
import { determineDefaultWorkspaceId, WorkspaceId } from "./workspaces.js";

/**
 * Builds the breadcrumb items a matched route contributes to the top-bar
 * trail. Set as `crumbs` on a route handle; the trail is the concatenation of
 * every matched route's items, so each route only adds its own segments —
 * typically one, with `match.pathname` as its link target. Dynamic labels are
 * entity-fetching `*CrumbLabel` components colocated with their features.
 *
 * The trail never shows the workspace: it starts at the item selected in the
 * left nav. So a workspace-root route contributes no crumb of its own unless
 * that root is itself a nav item (as projects are).
 */
export type BuildCrumbs = (match: UIMatch) => readonly BreadcrumbItem[];

const breadcrumbsHandle = z.object({
  crumbs: z.custom<BuildCrumbs>((value) => typeof value === "function"),
});

type BreadcrumbsHandle = z.infer<typeof breadcrumbsHandle>;

export function checkIsMatchWithBreadcrumbs<T extends UIMatch>(
  match: T,
): match is T & {
  handle: BreadcrumbsHandle;
} {
  return breadcrumbsHandle.safeParse(match.handle).success;
}

/**
 * The route-driven breadcrumb trail rendered in the app layout's top bar,
 * whose current crumb also titles the browser tab.
 */
export const AppBreadcrumbsContainer: FC = () => {
  const matches = useMatches();
  const trailRef = useRef<HTMLDivElement>(null);
  useBreadcrumbDocumentTitle(trailRef);

  // Projects are a one-off (as in AppNavigationContainer): the workspace
  // crumb's label and link follow the project's type, so it is prepended here
  // instead of coming from the static route handle.
  const projectMatch = useMatch(`${appPaths.project.pattern}/*`);
  const projectWorkspaceItem = useProjectWorkspaceCrumbItem(
    projectMatch?.params.projectId,
  );

  // The `/` route renders the job openings list for the recruiting default
  // workspace and redirects away otherwise (see HomePage). This can't come
  // from the route handle because the default workspace is feature-flag
  // driven, and flags are only readable from hooks.
  const usesDefaultWorkspace = matches
    .filter(checkIsMatchWithNavigation)
    .some((match) => match.handle.defaultWorkspace === true);
  const flags = useMainNavigationFeatureFlags();
  const defaultWorkspaceId = determineDefaultWorkspaceId(flags);

  const handleItems = usesDefaultWorkspace
    ? buildHomeCrumbs(defaultWorkspaceId)
    : matches
        .filter(checkIsMatchWithBreadcrumbs)
        .flatMap((match) => match.handle.crumbs(match));
  const items = projectWorkspaceItem
    ? [projectWorkspaceItem, ...handleItems]
    : handleItems;

  return (
    <div ref={trailRef} className="contents">
      {items.length > 0 ? <Breadcrumbs items={items} /> : null}
    </div>
  );
};

function buildHomeCrumbs(
  defaultWorkspaceId: WorkspaceId,
): readonly BreadcrumbItem[] {
  switch (defaultWorkspaceId) {
    case WorkspaceId.Performance:
    case WorkspaceId.Training:
      // HomePage redirects away for these workspaces.
      return [];
    default:
      // HomePage renders the job openings list.
      return [{ label: "Job openings" }];
  }
}
